import React, { useEffect, useState } from 'react';
import { NoDataOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { GlobalToken, theme } from 'antd';
import { useStores } from '@/hooks/use-stores';

import DevicesStatusTooltip from '../../MetricsTooltip/DevicesStatusTooltip';

const { useToken } = theme;

const getInitOptions = (token: GlobalToken) => ({
  chart: {
    type: 'xrange',
    backgroundColor: token.colorBgBase,
    zoomType: 'x',
    panning: {
      enabled: true,
    },
    panKey: 'alt',
    height: 60,
    margin: [0, 0, 0, 0],
    spacing: [0, 0, 0, 0],
    zooming: {
      resetButton: {
        theme: {
          style: {
            display: 'none',
          },
        },
      },
    },
    style: {
      fontFamily: 'Heebo',
    },
  },
  plotOptions: {
    series: {
      borderColor: 'transparent',
      borderRadius: 0,
      pointWidth: 10,
      turboThreshold: 10000,
    },
  },
  lang: { noData: '' },
  title: {
    align: 'left',
    x: 10,
    y: 45,
    style: {
      color: token.colorBorder,
      fontSize: 10,
      margin: 0,
      fontWeight: '500',
    },
  },
  noData: '' as NoDataOptions,
  credits: {
    enabled: false,
  },
  tooltip: {
    useHTML: true,
    borderColor: undefined,
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: undefined,
    hideDelay: 100,
    positioner: (_, __, point) => {
      const mouseGap = 12;
      const tooltipWidth = 260;
      const isRightSide = point.plotX > window.innerWidth / 2;

      return {
        y: point.plotY,
        x: point.plotX + (isRightSide ? (tooltipWidth + mouseGap) * -1 : mouseGap),
      };
    },
  },
  legend: {
    enabled: false,
  },
});

const DeviceStatusChart = observer(
  ({
    series,
    data,
    compressorName,
    highcharts,
  }: {
    series;
    data;
    highcharts;
    syncronizeCrossHairs?;
    compressorName?;
  }) => {
    const { token } = useToken();
    const initOptions = getInitOptions(token);
    const [options, setOptions] = useState<unknown>(initOptions);
    const { devicesStore } = useStores();

    useEffect(() => {
      const newOptions = {
        ...initOptions,
        title: {
          ...initOptions.title,
          text: compressorName,
        },
        tooltip: compressorName
          ? {
              ...initOptions.tooltip,
              formatter: function () {
                return ReactDOMServer.renderToString(
                  <DevicesStatusTooltip x={this.x} data={data} compressors={devicesStore?.compressors} token={token} />,
                );
              },
            }
          : { enabled: false },
        series: [
          {
            name: compressorName,
            data: series,
          },
        ],
        chart: {
          ...initOptions.chart,
          // events: {
          //   load: chart => syncronizeCrossHairs(chart?.target),
          // },
          ...(!compressorName && {
            height: 10,
          }),
        },
        xAxis: {
          maxPadding: 0,
          minPadding: 0,
          events: {
            afterSetExtremes: function (e) {
              if (e?.trigger === 'zoom') {
                highcharts.charts?.forEach(chart => {
                  if (chart && !isEqual(this, chart)) {
                    if (chart.options?.chart?.type === 'line' && chart.xAxis?.[0]?.min < this.chart.xAxis[0].min)
                      chart.showResetZoom();
                    chart.xAxis[0].setExtremes(this.chart.xAxis[0].min, this.chart.xAxis[0].max);
                  }
                });
              }
            },
          },
        },
      };

      setOptions(newOptions);
    }, [series]);

    return <HighchartsReact highcharts={highcharts} options={options} />;
  },
);

export default DeviceStatusChart;
